import type { ComponentType } from "react";
import type { OnboardingProgressInput, OnboardingSchoolInput } from "@/services/pipeline";

/**
 * The subset of the user profile the onboarding wizard reads to decide which
 * steps are complete and where to resume. Resume is derived entirely from
 * already-saved profile fields — never from localStorage.
 */
export interface OnboardingProfile {
  onboardingRole?: "player" | "supporter" | null;
  displayName?: string | null;
  gradYear?: number | null;
  state?: string | null;
  gender?: string | null;
  linkedJgsProfile?: string | null;
  hasJgsMapping?: boolean;
  isLinked?: boolean;
  academicSelectivity?: string | null;
  divisionsOpenTo?: string[] | null;
  preferredRegions?: string[] | null;
  costSensitivity?: string | null;
  weightedGpa?: number | null;
  satScore?: number | null;
  actScore?: number | null;
  preferredMajors?: string[] | null;
  preferredStates?: string[] | null;
  maxAnnualNetPrice?: number | null;
  schoolPreferencesNotes?: string | null;
  locationPreferenceMode?: "anywhere" | "home" | "nearby" | "regions" | "specific" | null;
}

/**
 * Data collected by the wizard that is NOT saved via the profile — it is sent
 * to the plan-generation call when onboarding finishes.
 */
export interface OnboardingWizardData {
  dreamSchools: OnboardingSchoolInput[];
  recruitingProgress: OnboardingProgressInput[];
}

export type OnboardingOutcome = "linked" | "skipped";

/**
 * A save function a step registers with the wizard. Called when the user clicks
 * the wizard's Continue button. Resolves `false` to block advancement (e.g. a
 * failed validation or a failed API call), `true` to advance.
 */
export type OnboardingSaveFn = () => Promise<boolean>;

export interface OnboardingStepProps {
  profile: OnboardingProfile;
  /** Report whether the step is currently valid (gates the Continue button). */
  onValidityChange: (valid: boolean) => void;
  /** Register (or clear with `null`) the save fn the wizard runs on Continue. */
  registerSave: (fn: OnboardingSaveFn | null) => void;
  /** Forwarded from the wizard; a JGS link should call this. */
  onLinkSuccess: (profileId: string) => void | Promise<void>;
  /**
   * A step may finish itself (e.g. the JGS step's own link/skip buttons). The
   * wizard advances to the next step, or finishes if this is the last step.
   */
  onStepComplete: (outcome?: OnboardingOutcome) => void;
  /**
   * Merge freshly saved answers into the wizard's working profile so later
   * steps see them (e.g. basics feeds the JGS search prefill and the school
   * typeahead's gender). The prop-passed profile is a mount-time snapshot.
   */
  updateProfile?: (patch: Partial<OnboardingProfile>) => void;
  /**
   * Merge a patch into the wizard's accumulated data (dream picks, recruiting
   * progress). This data is passed to `generateRecruitingPlan` on finish.
   */
  setWizardData: (patch: Partial<OnboardingWizardData>) => void;
  wizardData?: OnboardingWizardData;
}

export interface OnboardingProgress {
  version?: number;
  currentStepId?: string | null;
  completedStepIds?: string[];
  draft?: Partial<OnboardingWizardData>;
}

export interface OnboardingStep {
  id: string;
  title: string;
  Component: ComponentType<OnboardingStepProps>;
  isComplete: (profile: OnboardingProfile) => boolean;
  canSkip?: boolean;
  continueLabel?: string;
  /** The step renders its own terminal actions (for example JGS search/link). */
  managesOwnNavigation?: boolean;
}

/** Whether the profile is linked to a JGS ranking by any of the known signals. */
export function isProfileLinked(profile: OnboardingProfile): boolean {
  return !!(profile.isLinked || profile.hasJgsMapping || profile.linkedJgsProfile);
}
